import * as ft from 'funtypes'
import { ConnectorStandard, connectorCurrentType } from '../domain/connectors.js'
import type { VoltageInfo } from '../domain/types.js'
import type { DcWindowSource } from '../domain/voltage.js'
import { Latitude, Longitude, nullable, objectWith } from '../validation.js'

/**
 * The generated GoingElectric table: its shape, and how to read it.
 *
 * Written by `tools/ge-voltage-windows.ts` into `./table.generated.json`, which
 * is committed. Regenerating from an unchanged scrape must produce an unchanged
 * file - keys are sorted, nothing is timestamped, and provenance is a hash of
 * the input rather than the hour the generator ran - so a diff on that file is
 * always a real change in the data.
 *
 * Three tables, in descending order of how much they know about the connector
 * in front of them:
 *
 *  1. {@link GeVoltageTable.byEvseId} - the same socket, named by the same eMI3
 *     id in both registries. Nothing beats this and it covers about 5%.
 *  2. {@link GeVoltageTable.bySite} - the same address, connector picked by
 *     standard and nearest power. Covers about a third.
 *  3. {@link GeVoltageTable.windows} - not this connector at all, but every
 *     connector of its standard at its power, so the DC ceiling stops being the
 *     standard's theoretical maximum and becomes what Poland actually installed.
 *     Covers everything, and stays a derivation.
 *
 * What `volts` means depends on the current type, and the difference is the
 * whole reason this data is worth having. On DC it is the **ceiling**: the
 * highest voltage the charger will put on the cable, which is a property of the
 * hardware and is exactly what EIPA never records. On AC it is the **nominal**
 * line voltage, 230 or 400, which says how many phases are wired. `amps` is a
 * ceiling on both. The scrape bears this out: on DC, `volts * amps` exceeds the
 * declared kW on 96% of rows - two independent maxima, not a power rating - while
 * on AC, `volts * amps * sqrt(3)` lands within 10% of it on 96% of rows.
 */

// ---------------------------------------------------------------------------
// Leaves
// ---------------------------------------------------------------------------

/**
 * One connector's electrics as GoingElectric publishes them.
 *
 * `kw` is nullable because the join needs it and some rows lack it: a
 * declaration with no power can still be attached by EVSE id, which needs no
 * power to be sure, but never by address, which has nothing else to pick a
 * connector with.
 */
export const GeDeclaration = objectWith(
  {
    standard: ConnectorStandard,
    /** DC ceiling or AC nominal, in volts. Always present: rows without it are dropped. */
    volts: ft.Number,
  },
  {
    kw: ft.Number,
    amps: ft.Number,
  },
)
export type GeDeclaration = ft.Static<typeof GeDeclaration>

/**
 * What hardware of one standard, at one declared power, was observed to top out
 * at - together with how much of the sample agreed, so a caller can tell a
 * unanimous reading from a bare majority.
 */
export const GeWindow = objectWith(
  {
    /** The modal ceiling, in volts. */
    maxV: ft.Number,
    /** Rows in the group. */
    n: ft.Number,
    /** Share of the group carrying `maxV`/`maxAmps`, rounded to 2 decimals. */
    share: ft.Number,
  },
  {
    /** The modal current ceiling. Absent when no row in the group declared one. */
    maxAmps: ft.Number,
  },
)
export type GeWindow = ft.Static<typeof GeWindow>

/** A window that covers a power range rather than one declared value. */
export const GeBandWindow = ft.Intersect(
  GeWindow,
  ft.Object({
    /** Inclusive lower edge, in kW. */
    fromKw: ft.Number,
    /** Exclusive upper edge, in kW. `null` on the open-ended top band. */
    toKw: nullable(ft.Number),
  }),
)
export type GeBandWindow = ft.Static<typeof GeBandWindow>

export const GeStandardWindows = ft.Object({
  /** Keyed by {@link powerKey}. Only groups the generator judged large enough. */
  byKw: ft.Record(ft.String, GeWindow),
  /** Ordered, contiguous, and covering every power. The fallback for `byKw` misses. */
  byBand: ft.Array(GeBandWindow),
})
export type GeStandardWindows = ft.Static<typeof GeStandardWindows>

/**
 * One connector as scraped, kept whole rather than folded into a declaration.
 *
 * {@link GeSiteEntry.declarations} is deduplicated, because attaching voltage to
 * an EIPA connector only ever needs the distinct figures. Standing a site up on
 * its own needs the opposite: two 320 kW guns are two sockets a driver can plug
 * into, and folding them into one declaration would publish half a charger.
 */
export const GePlacePoint = objectWith(
  {
    standard: ConnectorStandard,
  },
  {
    kw: ft.Number,
    volts: ft.Number,
    amps: ft.Number,
    /**
     * As the operator punctuates it - `PL*GWI*E1218*1*1`, not the normalised
     * lookup key - because this one is published rather than joined on. It names
     * the site through `./naming.ts`, which reads the country and party out of
     * it by splitting on the separators, and it becomes the point's `code`. A
     * key here would silently cost every such site its operator name.
     */
    evseId: ft.String,
  },
)
export type GePlacePoint = ft.Static<typeof GePlacePoint>

/**
 * Everything needed to publish a site EIPA does not list, and nothing else.
 *
 * Present only where the scrape carried coordinates - a site that cannot be
 * placed cannot be a pin, however much it declares. The scraped `name` is
 * deliberately absent: `./naming.ts` builds one from these fields instead, and a
 * field that is not in the table cannot leak onto the map by accident.
 */
export const GePlace = objectWith(
  {
    lat: Latitude,
    lon: Longitude,
    /** Every connector at the site, in page order. */
    points: ft.Array(GePlacePoint),
  },
  {
    /** Street *including* the house number, as GoingElectric prints it. */
    street: ft.String,
    postcode: ft.String,
    city: ft.String,
  },
)
export type GePlace = ft.Static<typeof GePlace>

/** One scraped site's electrics, keyed in {@link GeVoltageTable.sites} by its id. */
export const GeSiteEntry = objectWith(
  {
    /** Distinct declarations across the site's connectors. */
    declarations: ft.Array(GeDeclaration),
  },
  {
    /** Absent for a scrape taken before the page function read coordinates. */
    place: GePlace,
  },
)
export type GeSiteEntry = ft.Static<typeof GeSiteEntry>

// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------

/** Why an address key could not be used, which is what a reader is told. */
export const GeContestedReason = ft.Union(
  /** Several stations share the address and disagree about the hardware. */
  ft.Literal('several-stations'),
  /** A listing at this address was hand-retired as no longer being there. */
  ft.Literal('listing-retired'),
)
export type GeContestedReason = ft.Static<typeof GeContestedReason>

/**
 * An address the table cannot speak for, kept so the *pipeline* can say so.
 *
 * The rest of this document records what we know; this records what we could not
 * settle. Without it a contested address is indistinguishable from an address
 * nobody scraped - both simply have no key - and the map would have no way to tell
 * a driver that several different stations are listed where they are standing.
 */
export const GeContestedAddress = ft.Object({
  reason: GeContestedReason,
  /** The GoingElectric sites claiming it. Two or more, except when retired. */
  siteIds: ft.Array(ft.String),
})
export type GeContestedAddress = ft.Static<typeof GeContestedAddress>

export const GeVoltageTable = ft.Object({
  /** Path of the scrape this was built from, relative to the repo root. */
  sourceFile: ft.String,
  /**
   * SHA-256 of that scrape. Provenance *and* the reason the output is stable:
   * there is no timestamp anywhere in this document, so an unchanged input
   * regenerates to an unchanged file and `git status` after the deploy's
   * regeneration step means the scrape really moved.
   */
  sourceSha256: ft.String,
  counts: ft.Object({
    sites: ft.Number,
    chargepoints: ft.Number,
    /** Rows that made it into a table. The rest are in the conflict report. */
    usable: ft.Number,
  }),
  /** Band edges in kW, matching `POWER_STOPS` in the map's power filter. */
  powerBands: ft.Array(ft.Number),
  /** Observed DC ceilings. DC standards only: on AC the grid fixes the voltage. */
  windows: ft.Record(ft.String, GeStandardWindows),
  /** {@link normalizeEvseId} -> the one declaration for that socket. */
  byEvseId: ft.Record(ft.String, GeDeclaration),
  /**
   * GoingElectric site id -> that site's declarations, so a match can be traced
   * back to `goingelectric.de/stromtankstellen/.../<id>/`.
   */
  sites: ft.Record(ft.String, GeSiteEntry),
  /**
   * Address key -> site id. A site has up to two keys (see `./keys.ts`), which is
   * why this is an index into {@link GeVoltageTable.sites} rather than the
   * declarations inline: storing them under both keys doubled the document.
   *
   * Keys that two GoingElectric sites both claimed are absent. An ambiguous key
   * is worse than no key: it would attach one operator's voltages to another's
   * connectors.
   */
  siteKeys: ft.Record(ft.String, ft.String),
  /**
   * Address keys deliberately left out of {@link GeVoltageTable.siteKeys}, and
   * why. A site whose address lands on one of these is flagged unconfirmed.
   */
  contested: ft.Record(ft.String, GeContestedAddress),
})
export type GeVoltageTable = ft.Static<typeof GeVoltageTable>

// ---------------------------------------------------------------------------
// Reading it
// ---------------------------------------------------------------------------

/**
 * The `byKw` key for a declared power.
 *
 * Rounded to one decimal on both sides of the join, so the `3.7` EIPA publishes
 * and the `3.7000000000000002` a float could produce cannot become two groups.
 */
export const powerKey = (powerKw: number): string => String(Math.round(powerKw * 10) / 10)

// ---------------------------------------------------------------------------
// Reading a declaration as electrics
// ---------------------------------------------------------------------------

/** `VoltageClass` splits AC at 300 V; the same number decides the phase count. */
const AC_CLASS_SPLIT_V = 300

/** Nominal line-to-line voltage of the Polish LV grid. Above it, nothing is AC. */
const AC_LINE_V = 400

/**
 * Whether a declaration describes AC, decided by the plug it names.
 *
 * The plug is asked rather than any derivation, because a declaration can arrive
 * for a connector nothing could be derived for at all - and reading a 400 V AC
 * nominal as a DC ceiling, or the other way round, is the one error in this data
 * that would produce a plausible-looking wrong number rather than a missing one.
 *
 * `TESLA` is the only standard whose current type the plug does not settle: Tesla
 * sells an AC Destination Charger and a DC Supercharger behind the same body.
 * There the voltage decides, and it decides cleanly - the grid supplies at most
 * 400 V, so anything above that is DC by arithmetic rather than by guesswork.
 */
export const geDeclaresAc = (standard: ConnectorStandard, volts: number): boolean => {
  const current = connectorCurrentType(standard)
  if (current !== 'unknown') return current === 'AC'
  return volts <= AC_LINE_V
}

/**
 * One declaration, read as the electrics it describes.
 *
 * On AC, the voltage is the *nominal* line voltage and therefore also states the
 * phase count: 400 V is three phases by definition of the Polish grid, 230 V is
 * one.
 *
 * On DC it is the figure the operator publishes for that socket, and it is
 * published as the figure rather than as the top of a range. Earlier this was
 * written as a derived `minV` plus the declared `maxV`, which read as "somewhere
 * between 200 V and 920 V" for a connector whose operator had said 920 V -
 * presenting a measured number as a guess, and a wide one. The derived floor is
 * inference about the *standard*; once a source has spoken for this specific
 * socket, mixing the two describes nothing real.
 *
 * `confidence` is the caller's to set, because it is the *join* that is strong or
 * weak, not the declaration. A site standing on this data alone has no join at
 * all and is therefore `high`: the source published these electrics for this very
 * socket, and there is no second registry the figure had to be matched into.
 */
export const geVoltageInfo = (
  declaration: GeDeclaration,
  confidence: VoltageInfo['confidence'],
): VoltageInfo => {
  const amps = declaration.amps === undefined ? {} : { amps: declaration.amps }

  if (geDeclaresAc(declaration.standard, declaration.volts))
    return {
      source: 'ge-declared',
      confidence,
      nominalV: declaration.volts,
      phases: declaration.volts > AC_CLASS_SPLIT_V ? 3 : 1,
      ...amps,
    }

  return { source: 'ge-declared', confidence, nominalV: declaration.volts, ...amps }
}

// ---------------------------------------------------------------------------
// Which declaration reaches which connector
// ---------------------------------------------------------------------------

/**
 * How far a declared power may drift before a declaration is refused a connector.
 *
 * The two registries round differently for the same hardware - a charger EIPA
 * records at 150 kW is 140 kW on GoingElectric, and both are describing the same
 * cabinet - and a voltage ceiling does not move over 10 kW anyway. Half is loose
 * enough to absorb that and tight enough to stop a site's 22 kW AC socket
 * collecting the 350 kW gun's declaration.
 *
 * It lives here, beside {@link GeDeclaration}, because two programs depend on it:
 * `./enrich.ts` applies it at match time, and `tools/ge-voltage-windows.ts` reasons
 * about it when deciding whether two sites sharing an address can be pooled. A
 * generator working to a different tolerance from the matcher would pool keys that
 * then produce answers the pooling was supposed to have ruled out.
 */
export const GE_MAX_POWER_DRIFT = 0.5

/**
 * Relative distance between a declared power and a connector's, or `undefined`
 * when the declaration carries no power and so can never be picked.
 */
export const gePowerDrift = (declaration: GeDeclaration, powerKw: number): number | undefined =>
  declaration.kw === undefined
    ? undefined
    : Math.abs(declaration.kw - powerKw) / Math.max(declaration.kw, powerKw, 1)

/**
 * Whether two declarations could ever be offered the same connector.
 *
 * A declaration of power `k` is eligible for a connector of power `P` exactly when
 * `|k - P| / max(k, P) <= GE_MAX_POWER_DRIFT`, which for a tolerance of one half
 * is the interval `P in [k/2, 2k]`. Two declarations therefore compete for some
 * connector iff those intervals overlap:
 *
 *     [k1/2, 2*k1] intersect [k2/2, 2*k2] != empty
 *       <=>  k1/2 <= 2*k2  and  k2/2 <= 2*k1
 *       <=>  k1 <= 4*k2    and  k2 <= 4*k1
 *
 * A factor of four, not of two - which is the trap. Comparing the two powers
 * against the tolerance directly (`|k1 - k2| / max <= 0.5`) tests whether each
 * would match *the other*, which is not the question and lets a competing pair
 * through: 100 kW and 350 kW fail that test yet both serve a 190 kW connector.
 *
 * Only used to reason about whether the identity of a declaration's site can
 * change an answer. It says nothing about which of two competing declarations
 * would win - that is nearest-kW, and it is `pickDeclaration`'s business.
 */
export const geDeclarationsCompete = (a: GeDeclaration, b: GeDeclaration): boolean => {
  if (a.standard !== b.standard) return false
  if (a.kw === undefined || b.kw === undefined) return false
  return a.kw <= 4 * b.kw && b.kw <= 4 * a.kw
}

/** Two declarations describing the same electrics. Order-independent. */
export const geDeclarationsAgree = (a: GeDeclaration, b: GeDeclaration): boolean =>
  a.volts === b.volts && a.amps === b.amps

/** One site's declarations, under an id that identifies it in a conflict report. */
export interface GePoolClaimant {
  readonly id: string
  readonly declarations: readonly GeDeclaration[]
}

/** The pair that makes pooling unsafe, with the sites they came from. */
export interface GePoolConflict {
  readonly left: GePoolClaimant['id']
  readonly leftDeclaration: GeDeclaration
  readonly right: GePoolClaimant['id']
  readonly rightDeclaration: GeDeclaration
}

/**
 * Whether several sites' declarations can be pooled under one address key.
 *
 * Returns the disagreement that makes it unsafe, or `undefined` when pooling
 * cannot change any answer.
 *
 * The reasoning, because the conclusion is unintuitive - an address shared by two
 * *different operators* is usually perfectly usable. This table is only ever asked
 * one question: what voltage and current does this connector run at.
 * `pickDeclaration` answers it by taking the same-standard declaration nearest in
 * power. So the identity of the site a declaration came from is irrelevant to the
 * answer *unless* two declarations from different sites could both be offered to
 * one connector and would answer differently. Where no such pair exists, pooling
 * is answer-preserving: whichever site the registry record really is, it gets the
 * same figures.
 *
 * Aleja Komisji Edukacji Narodowej 61 in Warsaw is a district office with an
 * Eleport charger and a ChargeEuropa charger in its car park, both 22 kW Type 2 at
 * 400 V and 32 A. Which of them a registry record refers to cannot change what we
 * publish, so the key is kept. Aleje Jerozolimskie 148 is a mall where Greenway
 * runs a 100 kW CCS gun at 920 V and Ekoenergetyka a 350 kW one at 1000 V: a
 * connector declared anywhere between 175 and 200 kW is eligible for both, so
 * there the key is dropped.
 *
 * **Only pairs from different claimants are compared**, and that is not an
 * optimisation. Two of one site's own declarations competing is not ambiguity: that
 * site's declarations are in the pool either way, so `pickDeclaration` resolves
 * them identically whether or not the key is shared. Comparing them would reject
 * pooling for a site that simply has an 11 kW socket beside a 22 kW one.
 */
export const geAddressPoolConflict = (
  claimants: readonly GePoolClaimant[],
): GePoolConflict | undefined => {
  for (let i = 0; i < claimants.length; i += 1) {
    for (let j = i + 1; j < claimants.length; j += 1) {
      const left = claimants[i]
      const right = claimants[j]
      if (left === undefined || right === undefined) continue

      for (const leftDeclaration of left.declarations) {
        for (const rightDeclaration of right.declarations) {
          if (!geDeclarationsCompete(leftDeclaration, rightDeclaration)) continue
          if (geDeclarationsAgree(leftDeclaration, rightDeclaration)) continue
          return { left: left.id, leftDeclaration, right: right.id, rightDeclaration }
        }
      }
    }
  }

  return undefined
}

/** The band covering a power, or `undefined` when the standard has no bands. */
const bandFor = (windows: GeStandardWindows, powerKw: number): GeBandWindow | undefined =>
  windows.byBand.find(
    (band) => powerKw >= band.fromKw && (band.toKw === null || powerKw < band.toKw),
  )

/**
 * A {@link DcWindowSource} over a loaded table: the exact declared power first,
 * then the band it falls in.
 *
 * The exact entry is preferred because power in this data is not a continuum. A
 * 50 kW CCS2 charger and a 60 kW one are two different products with two
 * different ceilings, and averaging them across a band loses precisely the
 * distinction the table exists to record. Bands only catch the long tail of odd
 * values - 148 kW, 155 kW - that no group of its own could ever be large enough
 * for.
 *
 * Returns `undefined` when neither covers the pair, which leaves
 * `dcSpecWindow`'s published window in place.
 */
export const geDcWindowSource =
  (table: GeVoltageTable): DcWindowSource =>
  (standard, powerKw) => {
    const windows = table.windows[standard]
    if (windows === undefined) return undefined

    const window = windows.byKw[powerKey(powerKw)] ?? bandFor(windows, powerKw)
    if (window === undefined) return undefined

    // A group with no observed current ceiling still contributes its voltage
    // ceiling, and the standard's rated current stays under it. The two are
    // independent: `maxAmps` only feeds the cross-check that catches a declared
    // power the interface cannot deliver, and that check is no less valid for
    // pairing an observed voltage with a published current.
    return window.maxAmps === undefined
      ? { maxV: window.maxV }
      : { maxV: window.maxV, maxAmps: window.maxAmps }
  }
